const toFinitePoint = point => {
  if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) {
    return null;
  }
  return {
    x: Number(point.x),
    y: Number(point.y),
  };
};

const MIN_POINT_DISTANCE = 2;
const CHAikin_ITERATIONS = 2;

const dedupePathPoints = points => {
  const nextPoints = Array.isArray(points)
    ? points.map(toFinitePoint).filter(Boolean)
    : [];

  if (nextPoints.length < 2) {
    return nextPoints;
  }

  return nextPoints.filter((point, index) => {
    if (index === 0) {
      return true;
    }
    const previous = nextPoints[index - 1];
    return (
      Math.hypot(point.x - previous.x, point.y - previous.y) >=
      MIN_POINT_DISTANCE
    );
  });
};

const simplifyPathPoints = points => {
  if (points.length <= 2) {
    return points;
  }

  const simplified = [points[0]];

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = simplified[simplified.length - 1];
    const current = points[index];
    const next = points[index + 1];

    const segmentDx = next.x - previous.x;
    const segmentDy = next.y - previous.y;
    const segmentLength = Math.hypot(segmentDx, segmentDy);

    if (segmentLength < MIN_POINT_DISTANCE) {
      continue;
    }

    const distanceToSegment = Math.abs(
      segmentDy * current.x -
        segmentDx * current.y +
        next.x * previous.y -
        next.y * previous.x,
    ) / segmentLength;

    if (distanceToSegment >= 0.75) {
      simplified.push(current);
    }
  }

  simplified.push(points[points.length - 1]);
  return simplified;
};

const smoothPathPoints = points => {
  if (points.length <= 2) {
    return points;
  }

  let nextPoints = points;

  for (let iteration = 0; iteration < CHAikin_ITERATIONS; iteration += 1) {
    if (nextPoints.length <= 2) {
      break;
    }

    const smoothed = [nextPoints[0]];

    for (let index = 0; index < nextPoints.length - 1; index += 1) {
      const current = nextPoints[index];
      const next = nextPoints[index + 1];

      smoothed.push({
        x: current.x * 0.75 + next.x * 0.25,
        y: current.y * 0.75 + next.y * 0.25,
      });
      smoothed.push({
        x: current.x * 0.25 + next.x * 0.75,
        y: current.y * 0.25 + next.y * 0.75,
      });
    }

    smoothed.push(nextPoints[nextPoints.length - 1]);
    nextPoints = smoothed;
  }

  return nextPoints;
};

export const normalizePathPoints = points => {
  const deduped = dedupePathPoints(points);
  if (deduped.length <= 2) {
    return deduped;
  }
  return simplifyPathPoints(deduped);
};

export const buildSmoothSvgPath = points => {
  const nextPoints = smoothPathPoints(normalizePathPoints(points));
  if (nextPoints.length < 2) {
    return '';
  }
  if (nextPoints.length === 2) {
    return `M ${nextPoints[0].x} ${nextPoints[0].y} L ${nextPoints[1].x} ${nextPoints[1].y}`;
  }

  const path = [`M ${nextPoints[0].x} ${nextPoints[0].y}`];

  for (let index = 0; index < nextPoints.length - 1; index += 1) {
    const p0 = nextPoints[index - 1] || nextPoints[index];
    const p1 = nextPoints[index];
    const p2 = nextPoints[index + 1];
    const p3 = nextPoints[index + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
  }

  return path.join(' ');
};
